<script setup lang="ts">
  import { computed } from 'vue';
  // Components
  import { IconButton, Tooltip } from '@/shared/ui';
  // Composables
  import { useDocumentActions } from '..';
  import { DocumentRevision, getDocumentRevisionStatusLabelForType } from '@/entities/Document';
  // Types
  import {
    DocumentType,
    StageLabels,
    getStageClasses,
    formatInstantToPtBR,
    formatCivilDateToPtBR,
  } from '@/shared/lib';
  // Utils
  import {
    getDocumentRevisionStatusClasses,
    getDocumentType,
    isStatusValidForDocumentType,
  } from '@/shared/lib/utils';
  // Icons
  import HistoryIcon from '@/shared/assets/icons/HistoryIcon.svg';
  import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import RenameIcon from '@/shared/assets/icons/RenameIcon.svg';

  const props = defineProps<{
    revision: DocumentRevision;
  }>();

  const { openModal, copyDocumentDetails } = useDocumentActions();

  const documentType = computed(() => getDocumentType(props.revision) ?? DocumentType.DOCUMENT);
  const isRegistration = computed(() => documentType.value === DocumentType.REGISTRATION);
  const isDocument = computed(() => documentType.value === DocumentType.DOCUMENT);

  const formattedStatus = computed(() =>
    getDocumentRevisionStatusLabelForType(props.revision, documentType.value)
  );

  const isValidStatus = computed(() =>
    isStatusValidForDocumentType(props.revision.status, documentType.value)
  );

  const translateType = (type: string | undefined) => {
    const translations: Record<string, string> = {
      [DocumentType.DOCUMENT]: 'Documento',
      [DocumentType.REGISTRATION]: 'Registro',
    };

    if (!type || !translations[type]) return '-';

    return translations[type];
  };
</script>

<template>
  <tr class="table-row-hover border-t border-gray-02 transition-colors duration-300 ease-out">
    <!-- Type -->
    <td class="pl-6 py-4.5 pr-0">
      <span
        class="px-3 py-1 rounded-full text-xs/3.5 font-medium inline-block w-full bg-gray-02 text-gray-07 text-bold text-center"
      >{{
        translateType(revision.document.documentIdentity?.type || DocumentType.DOCUMENT)
      }}</span>
    </td>
    <!-- Nome -->
    <td class="pl-6 py-4.5 pr-0">
      <div class="flex items-center gap-2">
        <div class="text-sm text-gray-07">
          {{
            revision.document.name && revision.document.prefix
              ? revision.document.prefix + ' - ' + revision.document.name
              : revision.document.name || revision.document.prefix
          }}
        </div>
      </div>
    </td>
    <!-- Revisão -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ revision.revision }}
    </td>
    <!-- Data da Vigência -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatCivilDateToPtBR(revision.effectiveDate) }}
    </td>
    <!-- Data de Criação -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatInstantToPtBR(revision.createdAt) }}
    </td>
    <!-- Data de Conclusão: TODO: Buscar a data correta -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatInstantToPtBR(revision.document.updatedAt) }}
    </td>
    <!-- Status -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-center">
      <div class="flex items-center">
        <span
          v-if="isValidStatus"
          :class="getDocumentRevisionStatusClasses(revision.status)"
          class="text-center"
        >
          {{ formattedStatus }}
        </span>
        <span v-else> - </span>
      </div>
    </td>
    <!-- Etapa -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-center">
      <div class="flex items-center">
        <span
          v-if="revision.stage && isRegistration"
          :class="getStageClasses(revision.stage)"
        >
          {{ StageLabels[revision.stage] }}
        </span>
        <span v-else> - </span>
      </div>
    </td>
    <!-- Ações -->
    <td class="px-6 py-4.5 text-sm text-gray-07">
      <div class="flex gap-2 items-center text-gray-xx-02">
        <Tooltip
          v-if="isDocument"
          content="Histórico"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="HistoryIcon"
            alt="History"
            @click="openModal(revision, 'history', { title: 'Histórico de documentos' })"
          />
        </Tooltip>
        <Tooltip
          v-if="isDocument"
          content="Treinamento"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="CertificateIcon"
            alt="Certificate"
            @click="openModal(revision, 'training', { title: 'Controle de treinamento' })"
          />
        </Tooltip>
        <Tooltip
          v-if="isDocument"
          content="Controle"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="SettingsIcon"
            alt="Settings"
            @click="openModal(revision, 'control', { title: 'Controle de documentos' })"
          />
        </Tooltip>
        <Tooltip
          v-if="isRegistration"
          content="Renomear"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="RenameIcon"
            alt="Rename"
          />
        </Tooltip>
        <Tooltip
          content="Copiar link"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="LinkIcon"
            alt="Link"
            @click="copyDocumentDetails(revision)"
          />
        </Tooltip>
      </div>
    </td>
  </tr>
</template>
